import type { editor } from "monaco-editor"
import Checkbox from "@/v2-ui/checkbox"
import Label from "@/v2-ui/label"
import Badge from "@/v2-backoffice-client/components/badge"
import { applyInspectorEdit } from "@/v2-backoffice-client/layer/update/inspector/layer.update.inspector.utils"
import {
  buildFilterControlValue,
  getFilterControlType,
  getFilterFieldNames,
} from "@/v2-backoffice-client/layer/update/inspector/layer.update.inspector.guided.utils"
import type { LayerServiceMetadata } from "@/v2-backoffice-client/layer/service/layer.service.metadata"

export type LayerUpdateInspectorGuidedFiltersProps = {
  editor: editor.IStandaloneCodeEditor
  filters: unknown[]
  metadata: LayerServiceMetadata
}

/**
 * Filter builder for the Filters blob: one row per filterable service
 * field, its console control type derived from the field type (string →
 * select, number → number-range, date → date-range). Checking appends a
 * starter control with infer-from-data config; unchecking removes the
 * field's control. Deeper tuning happens in the editor as usual.
 */
function LayerUpdateInspectorGuidedFilters(
  props: LayerUpdateInspectorGuidedFiltersProps,
) {
  const { editor: editorInstance, filters, metadata } = props

  const existing = getFilterFieldNames(filters)
  const filterable = metadata.fields
    .map((field) => ({ field, type: getFilterControlType(field) }))
    .filter(
      (
        entry,
      ): entry is {
        field: (typeof entry)["field"]
        type: NonNullable<(typeof entry)["type"]>
      } => entry.type !== null,
    )

  if (filterable.length === 0) {
    return (
      <p className="text-muted-foreground">
        The service has no filterable fields.
      </p>
    )
  }

  function toggle(
    field: (typeof filterable)[number]["field"],
    type: (typeof filterable)[number]["type"],
    checked: boolean,
  ) {
    if (checked) {
      applyInspectorEdit(
        editorInstance,
        [filters.length],
        buildFilterControlValue(field, type),
        { isArrayInsertion: true },
      )
      return
    }
    const index = filters.findIndex(
      (entry) =>
        entry &&
        typeof entry === "object" &&
        (entry as Record<string, unknown>).field === field.name,
    )
    if (index >= 0) {
      applyInspectorEdit(editorInstance, [index], undefined)
    }
  }

  return (
    <div className="flex max-h-64 flex-col gap-1 overflow-y-auto pr-1">
      {filterable.map(({ field, type }) => {
        const id = `guided-filter-${field.name}`
        return (
          <span key={field.name} className="flex items-center gap-2">
            <Checkbox
              id={id}
              checked={existing.includes(field.name)}
              onCheckedChange={(checked) => {
                toggle(field, type, checked === true)
              }}
            />
            <Label
              htmlFor={id}
              className="min-w-0 flex-1 cursor-pointer truncate text-xs font-normal"
              title={field.name}
            >
              {field.alias || field.name}
            </Label>
            <Badge variant="outline" size="tiny" className="shrink-0">
              {type}
            </Badge>
          </span>
        )
      })}
    </div>
  )
}

export default LayerUpdateInspectorGuidedFilters
